import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import AgoraRTC, {
  useRTCClient,
  useIsConnected,
  useClientEvent,
  LocalUser,
  useLocalCameraTrack,
  useLocalMicrophoneTrack,
  RemoteUser,
  useRemoteUsers,
  useJoin,
  usePublish,
} from "agora-rtc-react";
import {
  ref,
  set,
  push,
  update,
  remove,
  onValue,
  off,
  onDisconnect,
  serverTimestamp,
  get,
} from "firebase/database";
import { firebase_db } from "../../FirebaseSetup";
import ApiService from "../../services/ApiService";
import { authActions } from "../../redux/auth";
import ConfirmDialog from "../../components/ConfirmDialog";
import { toast } from "react-toastify";
import "./Call.css";
import { VipRequestOverlay } from "../../components/VipRequestOverlay";

const sanitizeChannelName = (name) => {
  return String(name || "live_room")
    .replace(/[^a-zA-Z0-9_-]/g, "_")
    .slice(0, 64);
};

function GoLiveStream() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userData, userId } = useSelector((state) => state.auth);

  // States derived from route parameters
  const initialRole = useMemo(() => {
    const r = location.state?.role;
    return r === "audience" || r === "host" ? r : "host";
  }, [location.state?.role]);

  const initialChannelName = useMemo(() => {
    const ch = location.state?.channelName;
    if (ch) return sanitizeChannelName(ch);
    const hostId = location.state?.hostId;
    if (hostId) {
      return sanitizeChannelName(`profile_live_${hostId}`);
    }
    return `live_${Date.now()}`;
  }, [location.state?.channelName, location.state?.hostId]);

  const hostId = useMemo(() => {
    return location.state?.hostId || userId;
  }, [location.state?.hostId, userId]);

  const hostName = useMemo(() => {
    return location.state?.hostName || "Host";
  }, [location.state?.hostName]);

  const hostProfileImage = useMemo(() => {
    return location.state?.hostImage || "/images/step/01.png";
  }, [location.state?.hostImage]);

  // Signaling & Agora state
  const [role, setRole] = useState(initialRole);
  const [channelName, setChannelName] = useState(initialChannelName);
  const [joined, setJoined] = useState(false);
  const [isHostLive, setIsHostLive] = useState(false);
  const [viewerCount, setViewerCount] = useState(0);
  const [viewerProfiles, setViewerProfiles] = useState([]);
  const [liveMessages, setLiveMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [audioMuted, setAudioMuted] = useState(false);
  const [videoMuted, setVideoMuted] = useState(false);
  const [walletAmount, setWalletAmount] = useState(Number(userData?.wallet_amount) || 0);

  // Stream violation protection policy states
  const [violationWarnings, setViolationWarnings] = useState(0);
  const [violationReason, setViolationReason] = useState("");
  const [showViolationWarningModal, setShowViolationWarningModal] = useState(false);
  const [isTerminated, setIsTerminated] = useState(false);

  const violationWarningsRef = useRef(0);
  useEffect(() => {
    violationWarningsRef.current = violationWarnings;
  }, [violationWarnings]);


  // VIP Room Request States
  const [isVipModalVisible, setIsVipModalVisible] = useState(false);
  const [vipPlans, setVipPlans] = useState([
    { id: "plan_15", minutes: 15, price: "$100" },
    { id: "plan_30", minutes: 30, price: "$200" },
    { id: "plan_45", minutes: 45, price: "$300" },
    { id: "plan_60", minutes: 60, price: "$400" },
  ]);
  const [confirmDialog, setConfirmDialog] = useState({ show: false, plan: null, type: null });
  const [insufficientDialog, setInsufficientDialog] = useState(false);
  const [pendingVipRequests, setPendingVipRequests] = useState([]);
  const [activeVipRequestId, setActiveVipRequestId] = useState(null);
  const [hostPrivateSession, setHostPrivateSession] = useState(null);
  const [remainingPrivateTime, setRemainingPrivateTime] = useState(0);
  const [isVipWaiting, setIsVipWaiting] = useState(false);
  const [hostAgoraUid, setHostAgoraUid] = useState(null);

  // Agora Hooks
  const agoraEngine = useRTCClient();
  const { localCameraTrack } = useLocalCameraTrack(role === "host" && joined);
  const { localMicrophoneTrack } = useLocalMicrophoneTrack(role === "host" && joined);
  const isConnected = useIsConnected();
  const remoteUsers = useRemoteUsers();

  const audienceSessionIdRef = useRef(
    `aud_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
  );
  const isTransitioningToPrivate = useRef(false);

  // Initialize view config for Agora
  useJoin(
    {
      appid: process.env.REACT_APP_AGORA_APP_ID || "",
      channel: channelName || "",
      token: null,
    },
    joined
  );

  // Conditionally publish broadcating tracks if host
  usePublish(
    [localMicrophoneTrack, localCameraTrack].filter(Boolean),
    isConnected && role === "host" && joined
  );

  // Debug Agora states in the console
  useEffect(() => {
    console.log("Agora GoLiveStream Debug Log:", {
      role,
      joined,
      isConnected,
      channelName,
      remoteUsersCount: remoteUsers.length,
      remoteUsers: remoteUsers.map(u => ({ uid: u.uid, hasVideo: u.hasVideo, hasAudio: u.hasAudio })),
      hostAgoraUid,
    });
  }, [role, joined, isConnected, channelName, remoteUsers, hostAgoraUid]);

  // Event handlers
  // Host leaves detection (audience)
  useClientEvent(agoraEngine, "user-left", (user) => {
    if (role === "audience" && user.uid === 0) {
      toast.warning("The live streaming session has ended by host.");
      endLive(false);
    }
  });

  // Subscribe to remote user streams automatically
  useClientEvent(agoraEngine, "user-published", async (user, mediaType) => {
    try {
      console.log("Agora: user-published", user.uid, mediaType);
      await agoraEngine.subscribe(user, mediaType);
    } catch (err) {
      console.error("Failed to subscribe to remote user", err);
    }
  });

  // Cleanup unsubscribed streams
  useClientEvent(agoraEngine, "user-unpublished", (user, mediaType) => {
    console.log("Agora: user-unpublished", user.uid, mediaType);
    // RemoteUser component will handle cleanup, but you can stop tracks if needed
    if (mediaType === "video" && user.videoTrack) {
      user.videoTrack.stop && user.videoTrack.stop();
    }
    if (mediaType === "audio" && user.audioTrack) {
      user.audioTrack.stop && user.audioTrack.stop();
    }
  });

  // Listeners setup
  const getHostStreamRef = useCallback(
    (hId) => ref(firebase_db, `/liveStreams/${hId}`),
    []
  );

  const getAudiencePresenceRootRef = useCallback(
    (channel) => ref(firebase_db, `/livePresence/${sanitizeChannelName(channel)}/members`),
    []
  );

  const getLiveMessagesRootRef = useCallback(
    (channel) => ref(firebase_db, `/livePresence/${sanitizeChannelName(channel)}/messages`),
    []
  );

  // Fetch Wallet amount
  const refreshProfileBalance = async () => {
    try {
      const response = await new ApiService().POST("/new-user-info", {
        userid: userId,
      });
      if (response.data.status === "1" && response.data.user_details?.length > 0) {
        const details = response.data.user_details[0];
        setWalletAmount(Number(details.wallet_amount) || 0);
        dispatch(authActions.UpdateUserData(details));
      }
    } catch (err) {
      console.log("Error loading user profile", err);
    }
  };

  useEffect(() => {
    refreshProfileBalance();
    loadPlansList();
  }, []);

  // Lock body scrolling to ensure perfect full screen fit
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    const originalHeight = document.body.style.height;
    document.body.style.overflow = "hidden";
    document.body.style.height = "100vh";

    return () => {
      document.body.style.overflow = originalOverflow === "hidden" ? "" : originalOverflow;
      document.body.style.height = originalHeight === "100vh" ? "" : originalHeight;
    };
  }, []);

  // Track refs to close active hardware camera and microphone tracks strictly on unmount to release resources
  const tracksRef = useRef({ camera: null, mic: null });
  useEffect(() => {
    tracksRef.current = { camera: localCameraTrack, mic: localMicrophoneTrack };
  }, [localCameraTrack, localMicrophoneTrack]);

  useEffect(() => {
    return () => {
      console.log("Releasing hardware tracks in GoLiveStream on unmount...");
      tracksRef.current.camera?.close();
      tracksRef.current.mic?.close();
    };
  }, []);

  // Fetch VIP private Plans from backend
  const loadPlansList = async () => {
    try {
      const response = await new ApiService().GET("/private-room-plan");
      if (response.data?.plans) {
        const rawPlans = response.data.plans;
        const mapped = rawPlans
          .map((plan, index) => {
            const minutes = Number(plan?.minutes ?? plan?.plan_minutes ?? plan?.duration);
            const amount = plan?.price ?? plan?.plan_amount ?? plan?.pay_amount;
            if (!minutes || !amount) return null;
            return {
              id: String(plan?.id ?? `plan_${minutes}_${index}`),
              minutes,
              price: `$${amount}`,
            };
          })
          .filter(Boolean);
        if (mapped.length > 0) {
          setVipPlans(mapped);
        }
      }
    } catch (err) {
      console.log("Error fetching private plans list", err);
    }
  };

  // Start live stream
  const startLive = async () => {
    const safeName = sanitizeChannelName(channelName);
    if (!safeName) {
      toast.error("Please provide a valid stream channel");
      return;
    }

    try {
      if (role === "host") {
        // Create live stream record in backend
        const startTime = Date.now();
        const response = await new ApiService().POST("/host-stream-create", {
          host_user_id: userId,
          room_id: safeName,
          starttime: startTime,
        });

        // Set Firebase stream node
        const hostStreamNode = getHostStreamRef(userId);
        await set(hostStreamNode, {
          hostId: userId,
          hostName: userData.name || "Host",
          profileImage: userData.profile_pic_pro_resize_200_url || "",
          profile_pic_pro_resize_200_url: userData.profile_pic_pro_resize_200_url || "",
          channelName: safeName,
          viewers: 0,
          isLive: true,
          warningCount: 0,
          startedAt: startTime,
          updatedAt: startTime,
        });

        // Clear any leftover private requests
        await remove(ref(firebase_db, `privateRoomRequests/${userId}`));

        // Set disconnect listener
        onDisconnect(hostStreamNode).remove();
        onDisconnect(ref(firebase_db, `/livePresence/${safeName}`)).remove();

        setIsHostLive(true);
        setJoined(true);
        toast.success("You are now live streaming!");
      } else {
        // Audience Join
        const startTime = Date.now();
        new ApiService().POST("/audience-join", {
          host_user_id: hostId,
          join_user_id: userId,
          room_id: safeName,
          starttime: startTime,
        }).catch(err => {
          console.error("Audience join backend registration failed:", err);
        });
        setJoined(true);
        toast.info(`Joined ${hostName}'s live stream`);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to connect to stream session.");
    }
  };

  // End live stream
  const endLive = async (isAcceptingPrivate = false) => {
    try {
      if (role === "host") {
        // Remove stream signaling nodes
        await remove(getHostStreamRef(userId));
        await remove(ref(firebase_db, `/livePresence/${channelName}`));

        if (!isAcceptingPrivate) {
          await remove(ref(firebase_db, `privateRoomRequests/${userId}`));
        }

        // Backend API leave stream
        await new ApiService().POST("/leave-stream", {
          user_id: userId,
          room_id: channelName,
          endtime: Date.now(),
        });
      } else {
        // Audience presence cleanup
        if (audienceSessionIdRef.current) {
          await remove(
            ref(firebase_db, `/livePresence/${channelName}/members/${audienceSessionIdRef.current}`)
          );
        }
      }
    } catch (err) {
      console.log("Error during stream teardown", err);
    } finally {
      try {
        if (agoraEngine) {
          await agoraEngine.leave?.();
          console.log("🧹 Explicitly left Agora channel in endLive");
        }
      } catch (e) {
        console.error("⚠️ Error leaving Agora channel in endLive:", e);
      }
      localCameraTrack?.close?.();
      localMicrophoneTrack?.close?.();

      setJoined(false);
      setIsHostLive(false);
      setLiveMessages([]);
      setViewerCount(0);
      setViewerProfiles([]);
      setIsVipWaiting(false);
      setHostAgoraUid(null);

      if (!isAcceptingPrivate) {
        navigate("/find-match");
      }
    }
  };

  // Watch presence counts and comment board
  useEffect(() => {
    if (!joined) return;

    // Viewers listener
    const presenceRef = getAudiencePresenceRootRef(channelName);
    const unsubscribePresence = onValue(presenceRef, (snapshot) => {
      const val = snapshot.val() || {};
      const count = Object.keys(val).length;
      setViewerCount(count);

      const profiles = Object.values(val).map((item, index) => ({
        userId: item.userId || `aud_${index}`,
        profileImage: item.profileImage || "",
      }));
      setViewerProfiles(profiles);

      if (role === "host") {
        update(getHostStreamRef(userId), {
          viewers: count,
          updatedAt: Date.now(),
        }).catch((e) => console.log(e));
      }
    });

    // Chat comments listener
    const messagesRef = getLiveMessagesRootRef(channelName);
    const unsubscribeMessages = onValue(messagesRef, (snapshot) => {
      const val = snapshot.val() || {};
      const sorted = Object.entries(val)
        .map(([id, message]) => ({
          id,
          senderName: message?.senderName || "Viewer",
          text: message?.text || "",
          senderRole: message?.senderRole || "audience",
          createdAt: Number(message?.createdAt) || 0,
        }))
        .filter((msg) => !!msg.text)
        .sort((a, b) => a.createdAt - b.createdAt)
        .slice(-40);
      setLiveMessages(sorted);
    });

    return () => {
      unsubscribePresence();
      unsubscribeMessages();
    };
  }, [joined, channelName, role, hostId, userId]);

  // Join audience presence registry
  useEffect(() => {
    if (!joined || role !== "audience") return;

    const memberRef = ref(
      firebase_db,
      `/livePresence/${channelName}/members/${audienceSessionIdRef.current}`
    );

    set(memberRef, {
      userId,
      name: userData.name || "Viewer",
      profileImage: userData.profile_pic_pro_resize_200_url || "",
      joinedAt: Date.now(),
    }).catch((e) => console.log("Audience presence registration error", e));

    onDisconnect(memberRef).remove();

    return () => {
      remove(memberRef).catch((e) => console.log(e));
    };
  }, [joined, channelName, role, userId]);

  // Host updates Firebase with their local Agora UID upon successful connection
  useEffect(() => {
    if (isConnected && role === "host" && userId && joined) {
      const localUid = agoraEngine.uid;
      if (localUid) {
        console.log("Web Host Agora UID connected:", localUid);
        update(ref(firebase_db, `/liveStreams/${userId}`), {
          agoraUid: localUid,
          updatedAt: Date.now(),
        }).catch((e) => console.log("Error updating host Agora UID", e));
      }
    }
  }, [isConnected, role, userId, joined, agoraEngine]);

  // Audience listens to the host stream node in Firebase to get host Agora UID and handle stream termination
  useEffect(() => {
    if (role !== "audience" || !hostId || !joined) return;

    const streamRef = getHostStreamRef(hostId);
    const unsubscribe = onValue(streamRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        if (data.status === "terminated" || data.warningCount >= 3 || data.terminationReason === "policy_violation") {
          setIsTerminated(true);
          toast.error("This stream has been terminated due to policy violations.");
          setTimeout(() => {
            endLive(false);
          }, 5000);
          return;
        }
        if (data.agoraUid) {
          console.log("Web Audience received host Agora UID:", data.agoraUid);
          setHostAgoraUid(data.agoraUid);
        }
        if (data.isLive === false) {
          toast.warning("The host has ended the live stream.");
          endLive(false);
        }
      } else {
        if (isTransitioningToPrivate.current) {
          console.log("ℹ️ Stream ended by host due to VIP session. Ignoring normal stream-end.");
          return;
        }
        // Node deleted means stream ended
        toast.warning("The live streaming session has ended by host.");
        endLive(false);
      }
    });

    return () => unsubscribe();
  }, [role, hostId, joined, getHostStreamRef]);

  // Send comment
  const sendChatMessage = async () => {
    const text = chatInput.trim();
    if (!text || !joined) return;

    try {
      const payload = {
        senderId: userId || audienceSessionIdRef.current,
        senderName: userData.name || "Viewer",
        senderImage: userData.profile_pic_pro_resize_200_url || "",
        senderRole: role,
        text,
        createdAt: Date.now(),
      };

      await set(push(getLiveMessagesRootRef(channelName)), payload);
      setChatInput("");
    } catch (err) {
      toast.error("Message not delivered.");
    }
  };

  // Mute audio
  const toggleMuteAudio = () => {
    setAudioMuted((prev) => !prev);
  };

  // Mute camera
  const toggleMuteVideo = () => {
    setVideoMuted((prev) => !prev);
  };

  // =================================================================
  // STREAM VIOLATION PROTECTION SYSTEM (SVPS) IMPLEMENTATION
  // =================================================================

  // 1. Host listens to their own stream warningCount inside Firebase to survive page-refreshes
  useEffect(() => {
    if (role !== "host" || !joined || !userId) return;

    const streamRef = getHostStreamRef(userId);
    const unsubscribe = onValue(streamRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        if (data.warningCount !== undefined) {
          setViolationWarnings(data.warningCount);
          if (data.warningCount > 0 && data.warningCount < 3) {
            setViolationReason(data.lastViolationReason || "Community Guidelines Violation");
            setShowViolationWarningModal(true);
          } else if (data.warningCount === 0) {
            setShowViolationWarningModal(false);
          }
        }
        if (data.status === "terminated" || data.warningCount >= 3) {
          setIsTerminated(true);
          setShowViolationWarningModal(false);
          toast.error("This stream has been terminated due to policy violations.");
          setTimeout(() => {
            endLive(false);
          }, 5000);
        }
      }
    });

    return () => unsubscribe();
  }, [role, joined, userId, getHostStreamRef]);

  // 2. Automated background check loop moderating host's Agora camera stream every 10 seconds
  useEffect(() => {
    if (role !== "host" || !joined || isTerminated) return;

    const moderationInterval = setInterval(() => {
      const isCameraInactiveNow = videoMuted || !localCameraTrack || (localCameraTrack && !localCameraTrack.enabled);
      if (isCameraInactiveNow) {
        console.log("SVPS: Camera is inactive/muted. Triggering violation...");
        triggerModerationViolation("Agora Camera Feed Offline / Muted");
      } else {
        // Capture active video frame from Agora stream and moderate using AI API
        captureAgoraFrameAndModerate();
      }
    }, 10000); // Check automatically every 10 seconds

    return () => {
      clearInterval(moderationInterval);
    };
  }, [role, joined, videoMuted, localCameraTrack, localCameraTrack?.enabled, isTerminated]);

  // Capture active video frame from Agora camera track and send to content moderation API
  const captureAgoraFrameAndModerate = async () => {
    try {
      const mediaStreamTrack = localCameraTrack?.getMediaStreamTrack?.();
      if (!mediaStreamTrack) return;

      const mediaStream = new MediaStream([mediaStreamTrack]);
      const video = document.createElement("video");
      video.srcObject = mediaStream;
      video.muted = true;
      video.setAttribute("playsinline", "true");
      video.style.display = "none";
      document.body.appendChild(video);

      const cleanup = () => {
        video.pause();
        video.srcObject = null;
        if (document.body.contains(video)) {
          document.body.removeChild(video);
        }
      };

      const doCapture = async () => {
        try {
          const width = video.videoWidth;
          const height = video.videoHeight;

          if (!width || !height) {
            console.log("SVPS Capture Skip: Video metadata dimensions not ready.");
            cleanup();
            return;
          }

          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(video, 0, 0, width, height);
          const frameBase64 = canvas.toDataURL("image/jpeg", 0.6);

          cleanup();

          // Send base64 frame to backend API for AI Moderation (Google Vision/AWS Rekognition)
          const response = await new ApiService().POST("/stream-frame-moderate", {
            host_user_id: userId,
            room_id: channelName,
            frame: frameBase64
          });

          if (response.data && response.data.violation === true) {
            const violationLabel = response.data.label || "Prohibited Explicit Content";
            console.log("SVPS Warning: Policy violation detected via AI Moderation:", violationLabel);
            triggerModerationViolation(`AI Moderation: ${violationLabel}`);
          } else {
            // Successfully verified compliance – reset warnings only if they are active
            if (violationWarningsRef.current > 0) {
              update(getHostStreamRef(userId), {
                warningCount: 0,
                updatedAt: Date.now()
              }).catch(e => console.log(e));
            }
          }
        } catch (err) {
          console.error("Frame processing or API check failed:", err);
          cleanup();
        }
      };

      video.onloadedmetadata = () => {
        video.play()
          .then(() => {
            setTimeout(doCapture, 500); // 500ms delay to guarantee frame pixel data is ready
          })
          .catch(err => {
            console.error("Failed to play video stream for capture:", err);
            cleanup();
          });
      };

    } catch (error) {
      console.error("Failed to capture video stream snapshot:", error);
    }
  };


  // Helper function to safely register violation and increment warningCount in database
  const triggerModerationViolation = async (reason = "Content Policy Violation") => {
    if (role !== "host" || !joined || isTerminated) return;

    try {
      const streamRef = getHostStreamRef(userId);
      const snapshot = await get(streamRef);
      const data = snapshot.val();

      if (data) {
        const currentWarnings = data.warningCount || 0;
        const nextWarnings = currentWarnings + 1;

        const updates = {
          warningCount: nextWarnings,
          lastViolationReason: reason,
          updatedAt: Date.now(),
        };

        if (nextWarnings >= 3) {
          updates.status = "terminated";
          updates.terminationReason = "policy_violation";
          updates.isLive = false;
        }

        await update(streamRef, updates);

        // Also save detailed audit logs in a subnode for DB/admin reference
        const logsRef = ref(firebase_db, `streamViolationLogs/${userId}`);
        await set(push(logsRef), {
          roomId: channelName,
          hostId: userId,
          warningNumber: nextWarnings,
          reason,
          createdAt: serverTimestamp(),
        });
      }
    } catch (err) {
      console.error("Failed to update SVPS state in database", err);
    }
  };


  // Host VIP incoming request listener
  useEffect(() => {
    if (role !== "host" || !joined) return;

    const requestsRef = ref(firebase_db, `privateRoomRequests/${userId}`);
    const unsubscribe = onValue(requestsRef, (snapshot) => {
      const val = snapshot.val() || {};
      const activeList = Object.entries(val)
        .map(([id, req]) => ({
          id,
          ...req,
        }))
        .filter((item) => item.status === "pending");
      setPendingVipRequests(activeList);
    });

    return () => unsubscribe();
  }, [role, joined, userId]);

  // Host accepts VIP room request
  const acceptVipRequest = async (request) => {
    try {
      const startTime = Date.now() + 2000;
      const endTime = startTime + request.minutes * 60 * 1000;

      // Backend API session join
      const joinResponse = await new ApiService().POST("/private-session-join", {
        request_id: request.vip_room_db_request_id,
        time: Date.now(),
      });

      // Update Firebase Signaling node
      const updates = {
        status: "approved",
        respondedAt: Date.now(),
        room_starttime: startTime,
        room_endtime: endTime,
        hostGender: userData.gender || "female",
      };

      await update(ref(firebase_db, `privateRoomRequests/${userId}/${request.id}`), updates);

      // ---- Clean up live stream before navigating ----
      await endLive(true);

      // Redirect to private call screen
      navigate("/private-video-room", {
        state: {
          channelName: request.channel_name,
          sender_id: request.req_user_id,
          receiver_id: request.host_user_id,
          sender_name: request.viewer_name,
          receiver_name: userData.name || "Host",
          receiver_image: userData.profile_pic_pro_resize_200_url || "",
          totalSeconds: request.minutes * 60,
          role: "host",
          isPrivateRoom: true,
          hostId: userId,
          liveChannelName: channelName,
          vip_room_db_request_id: request.vip_room_db_request_id,
        },
      });
    } catch (err) {
      console.log(err);
      toast.error("Failed to accept VIP room call.");
    }
  };

  // Host declines VIP room request
  const rejectVipRequest = async (request) => {
    try {
      await update(ref(firebase_db, `privateRoomRequests/${userId}/${request.id}`), {
        status: "rejected",
        respondedAt: Date.now(),
      });
      toast.info("VIP request declined.");
    } catch (err) {
      console.error(err);
      toast.error("Failed to decline VIP request.");
    }
  };


  // Viewer cancels their pending VIP request
  const cancelVipRequest = async () => {
    if (!activeVipRequestId) return;
    try {
      await remove(ref(firebase_db, `privateRoomRequests/${hostId}/${activeVipRequestId}`));
      setIsVipWaiting(false);
      setActiveVipRequestId(null);
      toast.info("VIP request cancelled.");
    } catch (err) {
      console.error(err);
      toast.error("Failed to cancel VIP request.");
    }
  };

  // Viewer requests VIP room session
  const submitVipRequest = async (plan) => {
    const priceStr = plan.price.replace(/[^0-9.]/g, "");
    const planPrice = Number(priceStr);

    // Dynamic Wallet Balance Verification (Requires at least 5 minutes balance)
    // Here we check if wallet contains enough to pay the chosen plan fee
    if (walletAmount < planPrice) {
      setInsufficientDialog(true);
      return;
    }

    try {
      setIsVipModalVisible(false);
      setIsVipWaiting(true);

      const vipRoomChannel = `private_${hostId}_${userId}_${Date.now()}`;

      // Backend Request Call
      const res = await new ApiService().POST("/private-req-create", {
        req_user_id: userId,
        host_user_id: hostId,
        stream_private_room_plan_id: plan.id,
        starttime: Date.now(),
        room_id: vipRoomChannel,
      });

      if (res.data.status === "1") {
        // Write request to Firebase database
        const requestId = push(ref(firebase_db, `privateRoomRequests/${hostId}`)).key;
        setActiveVipRequestId(requestId);

        const requestPayload = {
          channel_name: vipRoomChannel,
          host_user_id: String(hostId),
          minutes: plan.minutes,
          price: plan.price,
          req_user_id: userId,
          requested_at: Date.now(),
          status: "pending",
          stream_private_room_plan_id: plan.id,
          viewer_name: userData.name || "Viewer",
          vip_room_db_request_id: res.data.id,
        };

        await set(ref(firebase_db, `privateRoomRequests/${hostId}/${requestId}`), requestPayload);
        toast.info("VIP Private Call request sent successfully.");
      } else {
        setIsVipWaiting(false);
        toast.error("Failed to generate private room session.");
      }
    } catch (err) {
      setIsVipWaiting(false);
      console.log(err);
      toast.error("Failed to request private call.");
    }
  };

  // Viewer listens for acceptance of VIP request
  useEffect(() => {
    if (role !== "audience" || !activeVipRequestId) return;

    const requestRef = ref(firebase_db, `privateRoomRequests/${hostId}/${activeVipRequestId}`);
    const unsubscribe = onValue(requestRef, async (snapshot) => {
      const data = snapshot.val();
      if (data?.status === "approved") {
        isTransitioningToPrivate.current = true;
        setActiveVipRequestId(null); // Stop listener re-trigger
        setIsVipWaiting(false);

        // Teardown Live
        await endLive(true);

        // Navigate to private VIP video room screen
        navigate("/private-video-room", {
          state: {
            channelName: data.channel_name,
            sender_id: data.req_user_id,
            receiver_id: data.host_user_id,
            sender_name: userData.name || "Viewer",
            receiver_name: hostName,
            totalSeconds: data.minutes * 60,
            role: "audience",
            isPrivateRoom: true,
            hostId: data.host_user_id,
            vip_room_db_request_id: data.vip_room_db_request_id,
          },
        });
      } else if (data?.status === "rejected") {
        setActiveVipRequestId(null);
        setIsVipWaiting(false);
        toast.error("The host declined your VIP request.");
      }
    });

    return () => unsubscribe();
  }, [activeVipRequestId, role, hostId, navigate, hostName]);

  // Viewer listens to check if host goes busy in private room
  useEffect(() => {
    if (role !== "audience" || !joined) return;

    const requestsRef = ref(firebase_db, `privateRoomRequests/${hostId}`);
    const unsubscribe = onValue(requestsRef, (snapshot) => {
      const val = snapshot.val() || {};
      const now = Date.now();
      const busySession = Object.values(val).find(
        (item) => item.status === "approved" && item.room_endtime > now
      );

      setHostPrivateSession(busySession || null);
      if (busySession) {
        setRemainingPrivateTime(Math.max(0, Math.floor((busySession.room_endtime - now) / 1000)));
      }
    });

    return () => unsubscribe();
  }, [role, joined, hostId]);

  // Clock ticks down if host is busy in VIP session
  useEffect(() => {
    if (!hostPrivateSession) return;

    const timer = setInterval(() => {
      const now = Date.now();
      const secondsLeft = Math.max(0, Math.floor((hostPrivateSession.room_endtime - now) / 1000));
      setRemainingPrivateTime(secondsLeft);
      if (secondsLeft <= 0) {
        setHostPrivateSession(null);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [hostPrivateSession]);


  // -----------------------------------------------------------------
  // Cleanup – leave channel & close tracks on component unmount
  // -----------------------------------------------------------------
  useEffect(() => {
    return () => {
      console.log("🧹 Component unmount – leaving Agora channel");
      if (agoraEngine) {
        agoraEngine.leave?.();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="call-screen-wrapper">
      {/* HUD Top Bar Overlay */}
      {joined && !isTerminated && (
        <div className="call-header">
          <div className="call-title-area">
            <img src={hostProfileImage} className="call-avatar" alt="" />
            <div className="call-meta">
              <h5>{role === "host" ? "Broadcasting Live" : hostName}</h5>
              <span>
                {role === "host" ? `${viewerCount} watching` : "Public Live Stream"}
              </span>
            </div>
          </div>
          {role === "host" && violationWarnings > 0 && (
            <div className="violation-badge-hud" style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              backgroundColor: "rgba(15, 18, 24, 0.95)",
              border: "3px solid #ff2d55",
              color: "#ff2d55",
              padding: "30px 40px",
              borderRadius: "24px",
              fontWeight: 900,
              fontSize: "28px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "16px",
              boxShadow: "0 0 50px rgba(255, 45, 85, 0.6)",
              zIndex: 99999,
              pointerEvents: "none",
              textAlign: "center",
              width: "85%",
              maxWidth: "480px",
              backdropFilter: "blur(10px)",
              animation: "scaleIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)"
            }}>
              <i className="fa-solid fa-triangle-exclamation animate-bounce" style={{ fontSize: "56px" }}></i>
              <span style={{ fontSize: "36px", letterSpacing: "1px" }}>WARNING {violationWarnings}/3</span>
              <p style={{ fontSize: "14px", color: "#abb1b6", margin: "8px 0 0 0", fontWeight: 500, lineHeight: 1.5 }}>
                Your stream violates community policies.<br />
                Please remove the prohibited content immediately!
              </p>
            </div>
          )}
          <button className="call-close-btn" onClick={() => endLive(false)}>
            ✕
          </button>
        </div>
      )}

      {/* Main viewport */}
      {isTerminated ? (
        <div className="viewport-loading" style={{ backgroundColor: "#0c0e12", display: "flex", flexDirection: "column" }}>
          <div className="prejoin-icon" style={{ color: "#ff2d55", fontSize: "64px" }}>
            <i className="fa-solid fa-circle-xmark"></i>
          </div>
          <h2 style={{ fontWeight: 800, color: "#ff2d55", marginTop: "16px" }}>STREAM TERMINATED</h2>
          <p style={{ maxWidth: "450px", color: "#abb1b6", margin: "16px auto", fontSize: "16px", lineHeight: "1.6" }}>
            This stream has been terminated due to policy violations.
          </p>
        </div>
      ) : !joined ? (
        <div className="prejoin-container">
          <div className="prejoin-card">
            <div className="prejoin-icon">
              <i className="fa-solid fa-signal"></i>
            </div>
            <h3>{role === "host" ? "Go Live Streaming" : `Watch ${hostName}`}</h3>
            <p>
              {role === "host"
                ? "Starting a live stream allows online users to watch you in real time and interact with you through messages. All interactions must remain respectful and courteous at all times. Hosts earn up to 50% of engagement plan payout fees."
                : "Joining a public live stream is a privilege that allows you to view the host in real time and interact through text messages. As your messages are visible to all, Wheelucky strongly urges you to use respectful language."}
            </p>
            <button className="prejoin-action-btn" onClick={startLive}>
              {role === "host" ? "Start Streaming Now" : `Join Stream`}
            </button>
            <button className="prejoin-action-btn mt-4" onClick={() => navigate("/find-match")}>
              Back
            </button>
          </div>
        </div>
      ) : (
        <div className="video-stage">
          {/* Main Viewport */}
          {role === "host" ? (
            remoteUsers.find(u => u.hasVideo) ? (
              // Host sees remote participant in main screen if they publish video
              <div className="agora-video-view">
                <RemoteUser
                  user={remoteUsers.find(u => u.hasVideo)}
                  playVideo={true}
                  playAudio={true}
                  className="agora-video-view"
                />
              </div>
            ) : localCameraTrack ? (
              // Host sees themselves in main screen if alone or remote users don't have video
              <div className="agora-video-view">
                <LocalUser
                  videoTrack={localCameraTrack}
                  audioTrack={localMicrophoneTrack}
                  cameraOn={!videoMuted}
                  micOn={!audioMuted}
                  playVideo={true}
                  playAudio={false}
                  className="agora-video-view"
                />
              </div>
            ) : (
              <div className="viewport-loading">
                <p>Starting video tracks...</p>
              </div>
            )
          ) : remoteUsers.length > 0 ? (
            // Viewer sees the host in main screen
            (() => {
              const hostRemoteUser =
                remoteUsers.find(u => String(u.uid) === String(hostAgoraUid)) ||
                remoteUsers.find(u => u.hasVideo || u.hasAudio) ||
                remoteUsers[0];
              return (
                <div className="agora-video-view">
                  <RemoteUser
                    user={hostRemoteUser}
                    playVideo={true}
                    playAudio={true}
                    className="agora-video-view"
                  />
                </div>
              );
            })()
          ) : hostPrivateSession ? (
            // Viewer sees host is busy
            <div className="viewport-loading">
              <div className="prejoin-icon">
                <i className="fa-solid fa-lock" style={{ color: "#ffca28" }}></i>
              </div>
              <h4 style={{ fontWeight: 800 }}>
                {hostName} is in a VIP Session
              </h4>
              <p>
                Available in: {Math.floor(remainingPrivateTime / 60)}:
                {String(remainingPrivateTime % 60).padStart(2, "0")}
              </p>
            </div>
          ) : (
            // Viewer is waiting for host
            <div className="viewport-loading">
              <img
                src={hostProfileImage}
                style={{ width: "90px", height: "90px", borderRadius: "50%" }}
                alt=""
              />
              <p>Waiting for {hostName}'s video feed...</p>
            </div>
          )}

          {/* Small floating PIP for Host when a remote user with video is present */}
          {role === "host" && remoteUsers.some(u => u.hasVideo) && localCameraTrack && (
            <div className="local-pip-wrapper">
              <LocalUser
                videoTrack={localCameraTrack}
                audioTrack={localMicrophoneTrack}
                cameraOn={!videoMuted}
                micOn={!audioMuted}
                playVideo={true}
                playAudio={false}
                className="agora-video-view"
              />
              <div className="drag-tip">Me</div>
            </div>
          )}

          {/* Incoming VIP Request notifications for Hosts */}
          {role === "host" && pendingVipRequests.length > 0 && (
            <div className="vip-requests-overlay">
              {pendingVipRequests.map((req) => (
                <div className="vip-request-card" key={req.id}>
                  <h4 className="vip-card-header">
                    <i className="fa-solid fa-user"></i> {req.viewer_name}
                  </h4>
                  <div className="vip-card-body">
                    <h6>{req.minutes} min • {req.price}</h6>
                    <p>Request from {req.viewer_name}</p>
                  </div>
                  <div className="vip-actions">
                    <button className="btn accept" onClick={() => acceptVipRequest(req)}>
                      Accept &amp; Join
                    </button>
                    <button className="btn decline" onClick={() => rejectVipRequest(req)}>
                      Decline
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Real-time viewer count top HUD */}
          <div className="live-views-hud">
            <i className="fa-solid fa-eye"></i>
            <span>{viewerCount}</span>
          </div>

          {/* Comments board overlay left */}
          <div className="live-messages-overlay">
            <div className="live-messages-list">
              {liveMessages.map((msg) => (
                <div
                  className={`live-message-bubble ${msg.senderRole === "host" ? "host" : ""}`}
                  key={msg.id}
                >
                  <div className="message-sender">
                    {msg.senderName}
                    {msg.senderRole === "host" ? " (Host)" : ""}
                  </div>
                  <div className="message-text">{msg.text}</div>
                </div>
              ))}
            </div>
          </div>



          {/* Floating actions and chat bar */}
          <div className="live-input-box">
            <input
              type="text"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              placeholder="Send comments..."
              maxLength={150}
              onKeyDown={(e) => e.key === "Enter" && sendChatMessage()}
              disabled={!!hostPrivateSession || isVipWaiting}
            />
            <button className="live-send-btn" onClick={sendChatMessage}>
              <i className="fa-solid fa-paper-plane"></i>
            </button>
          </div>

          {/* Primary Controls overlay right bottom */}
          <div className="controls-bar controls-bar-stream">
            {role === "host" ? (
              <>
                <div className="control-item-wrap">
                  <button
                    className={`control-circle-btn ${audioMuted ? "" : "active"}`}
                    onClick={toggleMuteAudio}
                  >
                    <i className={`fa-solid ${audioMuted ? "fa-microphone-slash" : "fa-microphone"}`}></i>
                  </button>
                  <span className="control-btn-label">Audio</span>
                </div>
                <div className="control-item-wrap">
                  <button
                    className={`control-circle-btn ${videoMuted ? "" : "active"}`}
                    onClick={toggleMuteVideo}
                  >
                    <i className={`fa-solid ${videoMuted ? "fa-video-slash" : "fa-video"}`}></i>
                  </button>
                  <span className="control-btn-label">Cam</span>
                </div>
              </>
            ) : (
              isVipWaiting ? (
                <div className="control-item-wrap">
                  <button
                    className={`control-circle-btn active`}
                    style={{ backgroundColor: "#ffca28" }}
                    onClick={cancelVipRequest}
                  >
                    <i className="fa-solid fa-times" style={{ color: "#111" }}></i>
                  </button>
                  <span className="control-btn-label" style={{ color: "#ffca28", fontWeight: 700 }}>
                    VIP Call
                  </span>
                </div>
              ) : (
                <div className="control-item-wrap">
                  <button
                    className={`control-circle-btn active`}
                    style={{ backgroundColor: "#ffca28" }}
                    onClick={() => {
                      if (hostPrivateSession) {
                        toast.warning("The host is busy in a VIP session.");
                        return;
                      }
                      setIsVipModalVisible(true);
                    }}
                  >
                    <i className="fa-solid fa-crown" style={{ color: "#111" }}></i>
                  </button>
                  <span className="control-btn-label" style={{ color: "#ffca28", fontWeight: 700 }}>
                    VIP Call
                  </span>
                </div>
              )
            )}
            <div className="control-item-wrap">
              <button className="control-circle-btn danger" onClick={() => endLive(false)}>
                <i className="fa-solid fa-phone-slash"></i>
              </button>
              <span className="control-btn-label">Leave</span>
            </div>
          </div>
        </div>
      )}

      {/* Modal Dialog selection for VIP PLANS */}
      {isVipModalVisible && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0, 0, 0, 0.6)" }}
        >
          <div className="modal-dialog modal-dialog-centered vip-plans-modal">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">🔥 VIP Private Call Rooms</h5>
                <button
                  type="button"
                  className="close"
                  onClick={() => setIsVipModalVisible(false)}
                >
                  <span>✕</span>
                </button>
              </div>
              <div className="modal-body">
                <p style={{ fontSize: "14px", color: "#abb1b6", marginBottom: "20px" }}>
                  Select a premium private session duration. The host receives 50% of the plan pricing.
                </p>

                {vipPlans.map((plan) => (
                  <div
                    className="plan-row-web"
                    key={plan.id}
                    onClick={() => {
                      setConfirmDialog({ show: true, plan, type: 'vip' });
                    }}
                  >
                    <span className="plan-duration">{plan.minutes} Minutes Call</span>
                    <span className="plan-cost">{plan.price}</span>
                  </div>
                ))}

                <ConfirmDialog
                  show={confirmDialog.show}
                  title="Confirm VIP Room Call"
                  text={`Are you sure you want to request a private one-on-one room for ${confirmDialog.plan?.minutes} minutes (${confirmDialog.plan?.price})?`}
                  onConfirm={() => {
                    if (confirmDialog.plan) {
                      submitVipRequest(confirmDialog.plan);
                    }
                    setConfirmDialog({ show: false, plan: null, type: null });
                  }}
                  onCancel={() => setConfirmDialog({ show: false, plan: null, type: null })}
                />

                {/* Insufficient Funds Dialog */}
                <ConfirmDialog
                  show={insufficientDialog}
                  title="Insufficient Funds"
                  text={`Your wallet balance ($${walletAmount.toFixed(2)}) is less than the selected plan price. Please add funds to proceed.`}
                  onConfirm={() => {
                    navigate("/profile", { state: { tab_name: "funds" } });
                    setInsufficientDialog(false);
                  }}
                  onCancel={() => setInsufficientDialog(false)}
                />

                <div className="vip-disclaimer-box">
                  <h6>Disclaimer:</h6>
                  <p>
                    A VIP video session provides private one-on-one audio and video interaction.
                    All interactions must be courteous and polite. The caller pays the plan balance,
                    and the host receives 50% compensation.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Host Stream Violation Protection Overlay */}
      {role === "host" && showViolationWarningModal && (
        <div className="violation-warning-overlay">
          <div className="violation-warning-card">
            <div className="violation-warning-icon">
              <i className="fa-solid fa-triangle-exclamation animate-bounce"></i>
            </div>
            <h3>STREAM POLICY VIOLATION</h3>
            <p className="violation-warning-text" style={{ fontSize: "16px", color: "#ff2d55", fontWeight: 700, margin: "10px 0" }}>
              Warning {violationWarnings}/3: Your stream may violate community guidelines.
            </p>
            <p className="violation-warning-desc">
              Prohibited Activity: <strong>{violationReason}</strong>. Please remove the prohibited content or restore your active camera feed immediately to maintain compliance. The stream will automatically terminate on the 3rd warning.
            </p>
            {violationWarnings < 3 && (
              <button
                className="violation-action-btn"
                onClick={() => {
                  // In case camera was muted, turning it back on resets state in check interval
                  setVideoMuted(false);
                  setShowViolationWarningModal(false);

                  // Reset warnings in Firebase securely
                  update(getHostStreamRef(userId), {
                    warningCount: 0,
                    updatedAt: Date.now()
                  }).catch(e => console.log(e));
                }}
              >
                Restore Compliance
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default GoLiveStream;
